import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";
import type { Block } from "../types/objects.types";

type BackendBlock = {
	id: string;
	buildingId: string;
	name: string;
	floorsCount: number;
	image?: string | null;
	image3d?: string | null;
	blockSvg?: string | null;
};

type BackendResponse<T> = {
	success: true;
	data: T;
};

type BlocksListResponse = {
	items: BackendBlock[];
	meta: {
		limit: number;
		page: number;
		total: number;
		totalPages: number;
	};
};

export type BlockCreatePayload = {
	name: string;
	floorsCount: number;
	buildingId: string;
	image?: string;
	image3d?: string;
	blockSvg?: string;
};

export type BlocksListParams = {
	buildingId?: string;
	limit?: number;
	page?: number;
};

const mapBlock = (block: BackendBlock): Block => ({
	id: block.id,
	buildingId: block.buildingId,
	name: block.name,
	floorsCount: block.floorsCount,
	sectionsCount: 0,
	roomTypesCount: [],
	priceRange: "",
	areaRange: "",
	hasInteractivePlan: !!block.blockSvg,
	image: block.image || undefined,
	image3d: block.image3d || undefined,
	blockSvg: block.blockSvg || undefined,
});

function extractData<T>(response: { data: BackendResponse<T> | T }): T {
	const responseData = response.data;
	if (responseData && typeof responseData === "object" && "success" in responseData) {
		return (responseData as BackendResponse<T>).data;
	}
	return responseData as T;
}

export const blocksApi = {
	getBlocks: async (params: BlocksListParams = {}): Promise<Block[]> => {
		const response = await apiClient.get<BackendResponse<BlocksListResponse> | BlocksListResponse>(
			endpoints.objects.blocks,
			{ params }
		);
		const listResponse = extractData(response);
		return listResponse.items.map(mapBlock);
	},

	getBlock: async (id: string): Promise<Block> => {
		const response = await apiClient.get<BackendResponse<BackendBlock> | BackendBlock>(
			endpoints.objects.blockDetail(id)
		);
		const block = extractData(response);
		return mapBlock(block);
	},

	createBlock: async (payload: BlockCreatePayload): Promise<Block> => {
		const response = await apiClient.post<BackendResponse<BackendBlock> | BackendBlock>(
			endpoints.objects.blocks,
			payload
		);
		const block = extractData(response);
		return mapBlock(block);
	},

	updateBlock: async (id: string, payload: Partial<BlockCreatePayload>): Promise<Block> => {
		const response = await apiClient.patch<BackendResponse<BackendBlock> | BackendBlock>(
			endpoints.objects.blockDetail(id),
			payload
		);
		const block = extractData(response);
		return mapBlock(block);
	},

	deleteBlock: async (id: string): Promise<void> => {
		await apiClient.delete(endpoints.objects.blockDetail(id));
	},
};
